import type _jquery from "jquery";
import { computed, type ComputedRef, provide } from "vue";
import { exhaustiveGuard, isNil, isStringLiteralOfType } from "@mcwd/typescript-type-guards";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord
} from "@mcwd/util-vue";
import { GatingLevel } from "../types/definitions/gating/gating-level.js";
import { GatingState } from "../types/definitions/gating/gating-state.js";
import type { FormWidgetData } from "../../widgetDataTs.js";
import { useFormSettings } from "./use-form-settings.js";
import { useMarketoKnownVisitorState } from "./use-marketo-known-visitor-status.js";
import { useFormLogger } from "./use-form-logger.js";
import type { AnyFormSettingsKey } from "../form-settings/form-experience/form-settings-keys.js";
import { getInitialGatingLevel } from "../form-logic/gating/get-initial-gating-level.js";
import { UserGatingLevelCookieWrapper } from "../form-logic/gating/user-gating-level-cookie-wrapper.js";
import { getGatingStateFromDynamicValues } from "../form-logic/gating/get-gating-state-from-dynamic-values.js";

type UseGatingLevelInjectionValues<TAllowedGatingState extends GatingState.FormExperience = GatingState.FormExperience> = InjectionValuesRecord<{
  CurrentMarketoFormGatingState: ComputedRef<GatingState.PossibleValues.forMarketoForm<Extract<TAllowedGatingState, GatingState.MarketoForm>>>,
  CurrentFormExperienceGatingState: ComputedRef<GatingState.PossibleValues.forFormExperience<TAllowedGatingState>>,
  FormGatingLevel: Extract<TAllowedGatingState, GatingLevel.MarketoForm>;
  FormExperienceGatingLevel: Extract<TAllowedGatingState, GatingLevel.FormExperience>,
  setCookieGatingLevelState(gatingLevel: Extract<TAllowedGatingState, GatingLevel.UserCookie>): void;
}>;
type UseGatingLevelInjectionKeys<TAllowedGatingState extends GatingState.FormExperience = GatingState.FormExperience> = InjectionKeysRecord<UseGatingLevelInjectionValues<TAllowedGatingState>>;

const UseGatingLevelInjectionKeys = CreateInjectionKeysRecord<UseGatingLevelInjectionKeys>({
  CurrentMarketoFormGatingState: "CurrentMarketoFormGatingState",
  CurrentFormExperienceGatingState: "CurrentFormExperienceGatingState",
  FormGatingLevel: "FormGatingLevel",
  FormExperienceGatingLevel: "FormExperienceGatingLevel",
  setCookieGatingLevelState: "setCookieGatingLevelState",
});


type GatingLevelOptions = { widgetData: FormWidgetData, formSettingsKey: AnyFormSettingsKey, gatingLevelCustom?: (GatingLevel.FormExperience) | undefined };


export function initGatingLevel(options: GatingLevelOptions){
  if (checkValuesAlreadyProvided(UseGatingLevelInjectionKeys)) {
    return;
  }

  const { formSettingsKey, widgetData, gatingLevelCustom } = options;

  // const { logger } = useFormLogger();
  const { FormSettings } = useFormSettings({ callerLabel: "initGatingLevel" });
  const { allowedGatingStates, gatingCookiePermissions } = FormSettings;
  
  const initialMarketoFormGatingLevel = getInitialGatingLevel("MarketoForm", formSettingsKey, FormSettings, widgetData, gatingLevelCustom);
  const initialFormExperienceGatingLevel = getInitialGatingLevel("FormExperience", formSettingsKey, FormSettings, widgetData, gatingLevelCustom);

  if (isNil(initialMarketoFormGatingLevel) || allowedGatingStates.includes(initialFormExperienceGatingLevel) === false) {
    throw new Error(`Initial gating state "${initialFormExperienceGatingLevel}" does not exist in the allowedGatingStates: [${(allowedGatingStates).toString()}]`);
  }
  const { isMarketoKnownVisitor } = useMarketoKnownVisitorState({ callerLabel: "initGatingLevel" });
  const CookieGatingLevelState = computed(() => {
    if (gatingCookiePermissions.allowRead && isMarketoKnownVisitor.value === true) {
      const cookieGatingLevel = UserGatingLevelCookieWrapper.GatingLevel;
      switch (cookieGatingLevel) {
        case "NeedsForm": return "NeedsForm";
        case "FullGate": return isStringLiteralOfType("FullGate", allowedGatingStates)? "FullGate" : "NeedsForm";
        case "PartialGate": return isStringLiteralOfType("PartialGate", allowedGatingStates) ? "PartialGate" : "NeedsForm";
        default: exhaustiveGuard(cookieGatingLevel);
      }
    }
    return "NeedsForm";
  });

  function setCookieGatingLevelState(cookieGatingState: GatingLevel.UserCookie) {
    if (gatingCookiePermissions.allowWrite && GatingLevel.UserCookie.includes(cookieGatingState)) {
      UserGatingLevelCookieWrapper.GatingLevel = cookieGatingState;
    }
  }
  const CurrentMarketoFormGatingState = computed<GatingState.MarketoForm>(() => {
    const result = getGatingStateFromDynamicValues({
      type: "MarketoForm",
      initialGatingLevel: initialMarketoFormGatingLevel,
      isMarketoKnownVisitor,
      allowedGatingStates,
      cookieGatingLevelState: CookieGatingLevelState
    });
    return result;
  });

  const CurrentFormExperienceGatingState = computed<GatingState.FormExperience>(() => {
    const result = getGatingStateFromDynamicValues({
      type: "FormExperience",
      initialGatingLevel: initialFormExperienceGatingLevel,
      isMarketoKnownVisitor,
      allowedGatingStates,
      cookieGatingLevelState: CookieGatingLevelState
    });
    return result;
  });
  provide(UseGatingLevelInjectionKeys.CurrentMarketoFormGatingState, CurrentMarketoFormGatingState);
  provide(UseGatingLevelInjectionKeys.CurrentFormExperienceGatingState, CurrentFormExperienceGatingState);
  provide(UseGatingLevelInjectionKeys.FormGatingLevel, initialMarketoFormGatingLevel);
  provide(UseGatingLevelInjectionKeys.FormExperienceGatingLevel, initialFormExperienceGatingLevel);
  provide(UseGatingLevelInjectionKeys.setCookieGatingLevelState, setCookieGatingLevelState);
}

export function useGatingLevel<
  TAllowedGatingState extends GatingState.FormExperience = GatingState.FormExperience
> (
  { callerLabel }: { callerLabel?: string | undefined } = {}
)
{
  const { logger } = useFormLogger({ callerLabel: "useGatingLevel" });
  try {
    callerLabel ??= "useGatingLevel";
    const injectionKeys = UseGatingLevelInjectionKeys as UseGatingLevelInjectionKeys<TAllowedGatingState>;
    const CurrentMarketoFormGatingState = injectRequiredWithSelf(injectionKeys.CurrentMarketoFormGatingState);
    const CurrentFormExperienceGatingState = injectRequiredWithSelf(injectionKeys.CurrentFormExperienceGatingState);
    const FormGatingLevel = injectRequiredWithSelf(injectionKeys.FormGatingLevel);
    const FormExperienceGatingLevel = injectRequiredWithSelf(injectionKeys.FormExperienceGatingLevel);
    const setCookieGatingLevelState = injectRequiredWithSelf(injectionKeys.setCookieGatingLevelState);
    return { CurrentMarketoFormGatingState, CurrentFormExperienceGatingState, FormGatingLevel, FormExperienceGatingLevel, setCookieGatingLevelState };
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}